import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Card, Col, Row } from 'antd';
import { Permissions } from '../../../../../const/permissions';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import urlPageDynamicNews from '../../../../../urls/urlPageDynamicNews';
import { HasRights } from '../../../../../components/HasRights/HasRights';
import useCheckMobileScreen from '../../../../../app/hooks/useCheckMobileScreen';
import applyFilter from '../../../../../lib/applyFilter';
import handleResponse from '../../../../../lib/handleResponse';
import getPriorityUpdateButtonTooltipMessage from '../../../../../lib/getPriorityUpdateButtonTooltipMessage';
import Spinner from '../../../../../components/Spinner';
import BreadCrumbs from '../../../../../layout/components/breadcrumbs';
import DocumentationTooltip from '../../../../../components/DocumentationTooltip/DocumentationTooltip';
import DynamicNewsPriority from '../../../components/DynamicNewsPriority';
import PrioritiesTitle from '../../../../../components/PrioritiesTitle';
import PriorityFilter from '../../../../../components/PriorityFilter';
import { DATE_FORMAT } from '../../../../../const/system';
import { useGetDynamicNewsPriorityQuery, useUpdateDynamicNewsPriorityMutation } from '../../../api/dynamicNewsApiSlice';



const PageDynamicNewsPriority = () => {
  const intl = useIntl();
  const [ dynamicNewsOrder, setDynamicNewsOrder ] = useState([]);
  const [ searchQueryParams, setSearchParams ] = useSearchParams();
  const searchFilterFields = [ 'date' ];
  const isMobile = useCheckMobileScreen();

  const { data: news = { data: [], pagination: {} }, isFetching } = useGetDynamicNewsPriorityQuery({
    queryParams: searchQueryParams.get('search') ? `search=${searchQueryParams.get('search')}` : '',
  });

  const [ updateNewsPriority, { isLoading: isUpdatingNewsPriority } ] = useUpdateDynamicNewsPriorityMutation();

  const today = dayjs().format(DATE_FORMAT);
  const search = searchQueryParams.get('search');
  const date = search ? search.split(':')[1] : today;
  const hasNoOrderChanges = !dynamicNewsOrder.length;

  const onDynamicNewsOrderChange = (order) => {
    setDynamicNewsOrder(order);
  };

  const submitDynamicNewsPriority = () => {
    const order = dynamicNewsOrder.map((item) => {
      const position = Number(item) - 1;

      return news.data[position]?.id;
    });

    updateNewsPriority({ order, date }).unwrap()
      .then(() => handleResponse(
        'success',
        intl,
        'ui-general-updated-successfully',
        [
          () => setDynamicNewsOrder([]),
        ],
      ))
      .catch((error) => handleResponse('error', intl, 'ui-general-update-failed', [], error));
  };

  const isLoading = isFetching || isUpdatingNewsPriority;

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >

        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPageDynamicNews()}>
              <IntlMessages id='dynamic-news-breadcrumbs' />
            </Link>
          }
          breadCrumbActive={
            <div>
              <IntlMessages id='ui-general-priority' />
              <DocumentationTooltip link="https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/623149125/Dynamic+news+priority" />
            </div>
          }
        />
      </Row>

      <Row gutter={[ 16, 16 ]} style={{ flexDirection: isMobile ? 'column-reverse' : 'row' }}>
        <Col sm={24} md={16}>
          <Card
            className="hp-border-color-black-40 hp-card-6"
            title={(
              <PrioritiesTitle
                isDisabled={hasNoOrderChanges}
                hasPermission={HasRights([ Permissions.CONTENT.NEWS.PRIORITY ])}
                title={intl.formatMessage({ id: 'news-priority-title' })}
                onAction={submitDynamicNewsPriority}
                tooltipMessage={getPriorityUpdateButtonTooltipMessage(hasNoOrderChanges)}
              />
            )}
          >
            <Spinner spinning={isLoading}>
              <DynamicNewsPriority
                news={news?.data}
                isLoading={isLoading}
                onOrderChange={onDynamicNewsOrderChange}
              />
            </Spinner>
          </Card>
        </Col>
        <Col sm={24} md={8}>
          <Card
            title={intl.formatMessage({ id: 'news-priority-filter-title' })}
            className="hp-border-color-black-40 hp-card-6"
          >
            <PriorityFilter
              onCancel={() => {}}
              isSubmitting={isLoading || isUpdatingNewsPriority}
              initialValues={searchQueryParams.get('search') ? { date } : {}}
              onSubmit={(values) => {
                applyFilter({
                  values,
                  searchQueryParams,
                  searchFilterFields,
                  setSearchParams,
                  callbacks: [ () => setDynamicNewsOrder([]) ],
                });
              }}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageDynamicNewsPriority;
